/**
 * voiceRecorderMixin — MediaRecorder-backed voice note capture for the chat composer.
 *
 * Spread into an Alpine component that also provides `selectedFile`, `fileError`
 * and `sendMessage()` (i.e. messageInput). Finishing a recording drops the blob
 * into `selectedFile` and hands off to the existing attachment send path, so
 * voice notes travel the same multipart upload as any other attachment.
 */

const WAVEFORM_BAR_COUNT = 40;
const LEVEL_SAMPLE_INTERVAL_MS = 100;
const TIMER_INTERVAL_MS = 200;
// Matches Chat::Message::AUDIO_SIZE_LIMIT. Timeslice flushes encoded data while
// recording so we can stop before the blob would fail the 20 MB send check.
const MAX_RECORDING_BYTES = 20 * 1024 * 1024;
const RECORDING_SIZE_HEADROOM_BYTES = 256 * 1024;
const DATA_TIMESLICE_MS = 1000;
const LIMIT_NOTICE_MS = 8000;
// Peaks stored with the message so the bubble can draw a waveform without
// downloading the audio. Must stay within Chat::Message::WAVEFORM_MAX_PEAKS.
const STORED_PEAK_COUNT = 48;
const STORED_PEAK_MAX = 100;

// Averages the recorded levels into a fixed number of buckets, so a 3-second
// note and a 3-minute one both produce the same compact array.
function downsamplePeaks(levels) {
  if (!levels.length) return [];

  return Array.from({ length: STORED_PEAK_COUNT }, (_, bucket) => {
    const start = Math.floor((bucket * levels.length) / STORED_PEAK_COUNT);
    const end = Math.max(start + 1, Math.floor(((bucket + 1) * levels.length) / STORED_PEAK_COUNT));
    const slice = levels.slice(start, end);
    const mean = slice.reduce((sum, level) => sum + level, 0) / slice.length;

    return Math.round(mean * STORED_PEAK_MAX);
  });
}

// Browsers disagree on what MediaRecorder can produce: Chrome and Firefox give
// Opus-in-WebM, Safari only offers MP4/AAC. Each entry pairs the recorder's
// mimeType with the bare content type the server allowlists and the extension
// Cloudinary needs in order to classify the upload as a media (not raw) asset.
const RECORDING_FORMATS = [
  { mimeType: 'audio/webm;codecs=opus', contentType: 'audio/webm', extension: 'webm' },
  { mimeType: 'audio/webm', contentType: 'audio/webm', extension: 'webm' },
  { mimeType: 'audio/mp4', contentType: 'audio/mp4', extension: 'm4a' },
  { mimeType: 'audio/ogg;codecs=opus', contentType: 'audio/ogg', extension: 'ogg' },
];

function audioContextClass() {
  return window.AudioContext || window.webkitAudioContext;
}

// AudioContext counts as a recording requirement, not an optional extra: it is
// what captures the peaks, and every voice message is expected to carry them.
function pickRecordingFormat() {
  if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) return null;
  if (!audioContextClass()) return null;

  return RECORDING_FORMATS.find(({ mimeType }) => MediaRecorder.isTypeSupported(mimeType)) || null;
}

export function formatRecordingTime(totalSeconds) {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

export function voiceRecorderMixin({ recorderErrorMessages = {} } = {}) {
  return {
    recorderErrorMessages,
    isRecording: false,
    isPaused: false,
    recordingLimitReached: false,
    recordingNotice: null,
    recordingSeconds: 0,
    // Rolling window of recent input levels (0–1), newest last.
    recordingLevels: [],
    // Peaks and length of the finished recording, sent alongside the audio.
    recordedWaveform: null,
    recordedDuration: null,

    // Methods, not getters: this mixin is object-spread into messageInput, and
    // spread snapshots getters as their current value (`"0:00"`).
    recordingTime() {
      return formatRecordingTime(this.recordingSeconds);
    },

    async startRecording() {
      if (this.isRecording) return;

      const format = pickRecordingFormat();
      if (!format) {
        this.fileError = this.recorderErrorMessages.notSupported;
        return;
      }

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        this.fileError = err?.name === 'NotAllowedError' || err?.name === 'SecurityError'
          ? this.recorderErrorMessages.permissionDenied
          : this.recorderErrorMessages.recordingFailed;
        return;
      }

      this.clearFile();
      this.closeEmojiPicker();
      this.fileError = null;
      this.recordingNotice = null;
      this.recordingLimitReached = false;

      this._recordingFormat = format;
      this._recordingStream = stream;
      this._recordingChunks = [];
      this._recordedBytes = 0;

      const recorder = new MediaRecorder(stream, { mimeType: format.mimeType });
      recorder.ondataavailable = (event) => {
        if (event.data?.size) {
          this._recordingChunks.push(event.data);
          this._recordedBytes += event.data.size;
        }
        if (!this._finishing && !this.recordingLimitReached
            && this._recordedBytes >= MAX_RECORDING_BYTES - RECORDING_SIZE_HEADROOM_BYTES) {
          this.capRecording();
        }
      };
      recorder.start(DATA_TIMESLICE_MS);
      this._mediaRecorder = recorder;

      this.isRecording = true;
      this.isPaused = false;
      this.recordingSeconds = 0;
      this.recordingLevels = [];
      this._allLevels = [];
      this._elapsedBeforePause = 0;
      this._segmentStartedAt = Date.now();

      this._startRecordingTimer();
      this._startLevelMeter(stream);
      this.trackChat('voice message', 'record start');
    },

    pauseRecording() {
      if (!this.isRecording || this.isPaused || this.recordingLimitReached) return;
      this._mediaRecorder?.pause();
      this._elapsedBeforePause += Date.now() - this._segmentStartedAt;
      this.isPaused = true;
    },

    resumeRecording() {
      if (!this.isRecording || !this.isPaused || this.recordingLimitReached) return;
      this._mediaRecorder?.resume();
      this._segmentStartedAt = Date.now();
      this.isPaused = false;
    },

    toggleRecordingPause() {
      if (this.isPaused) {
        this.resumeRecording();
      } else {
        this.pauseRecording();
      }
    },

    cancelRecording() {
      if (!this.isRecording || this._finishing) return;

      const recorder = this._mediaRecorder;
      if (recorder && recorder.state !== 'inactive') {
        // Drop the data handler first so the discarded chunks are never assembled.
        recorder.ondataavailable = null;
        recorder.stop();
      }
      this._recordingChunks = [];
      this._teardownRecording();
      this.trackChat('voice message', 'cancel');
    },

    // Size cap: freeze this take so it can still be sent, but do not upload it.
    async capRecording() {
      if (!this.isRecording || this.recordingLimitReached || this._finishing) return;
      this.recordingLimitReached = true;
      this._elapsedBeforePause += Date.now() - this._segmentStartedAt;
      this.isPaused = true;
      this.recordedDuration = this._elapsedSeconds();
      this.recordingSeconds = Math.floor(this.recordedDuration);
      this.recordedWaveform = downsamplePeaks(this._allLevels);
      this._showLimitNotice();

      await this._stopRecorder();
      this._stopCapture();
    },

    // Stopping the recorder is async, and tapping send twice can reach here,
    // so the flag keeps a recording from being sent twice.
    async finishRecording() {
      if (!this.isRecording || this._finishing) return;
      this._finishing = true;

      const format = this._recordingFormat;
      // Both are read off recording state that teardown clears, so capture first.
      const duration = this.recordedDuration ?? this._elapsedSeconds();
      const waveform = this.recordedWaveform ?? downsamplePeaks(this._allLevels);

      const blob = await this._stopRecorder();
      this._teardownRecording();

      if (!blob?.size) return;

      this.recordedWaveform = waveform;
      this.recordedDuration = duration;
      this.selectedFile = new File([blob], `voice-message.${format.extension}`, {
        type: format.contentType,
      });
      this.sendMessage();
    },

    clearRecordedAudio() {
      this.recordedWaveform = null;
      this.recordedDuration = null;
    },

    destroyVoiceRecorder() {
      this.cancelRecording();
    },

    _stopRecorder() {
      return new Promise((resolve) => {
        const recorder = this._mediaRecorder;
        const assemble = () => {
          const chunks = this._recordingChunks;
          resolve(chunks.length
            ? new Blob(chunks, { type: this._recordingFormat.contentType })
            : null);
        };

        if (!recorder || recorder.state === 'inactive') {
          assemble();
          return;
        }

        recorder.onstop = assemble;
        recorder.stop();
      });
    },

    _showLimitNotice() {
      this.recordingNotice = this.recorderErrorMessages.limitReached;
      clearTimeout(this._noticeTimeoutId);
      this._noticeTimeoutId = setTimeout(() => {
        this.recordingNotice = null;
        this._noticeTimeoutId = null;
      }, LIMIT_NOTICE_MS);
    },

    // Elapsed recorded time, excluding any paused stretches.
    _elapsedSeconds() {
      const currentSegment = this.isPaused ? 0 : Date.now() - this._segmentStartedAt;
      return (this._elapsedBeforePause + currentSegment) / 1000;
    },

    _startRecordingTimer() {
      this._recordingTimerId = setInterval(() => {
        if (this.isPaused) return;

        const elapsed = this._elapsedSeconds();
        this.recordingSeconds = Math.floor(elapsed);
      }, TIMER_INTERVAL_MS);
    },

    // Drives the live waveform from the raw mic signal rather than the encoded
    // stream, which is the only way to show levels while recording is in flight.
    _startLevelMeter(stream) {
      const audioContext = new (audioContextClass())();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      audioContext.createMediaStreamSource(stream).connect(analyser);

      const samples = new Uint8Array(analyser.frequencyBinCount);
      let lastSampleAt = 0;

      const sample = (timestamp) => {
        this._levelFrameId = requestAnimationFrame(sample);
        if (this.isPaused || timestamp - lastSampleAt < LEVEL_SAMPLE_INTERVAL_MS) return;
        lastSampleAt = timestamp;

        analyser.getByteTimeDomainData(samples);
        // Byte time-domain data is centred on 128, so deviation from it is amplitude.
        const sumOfSquares = samples.reduce((sum, value) => sum + ((value - 128) / 128) ** 2, 0);
        const level = Math.min(1, Math.sqrt(sumOfSquares / samples.length) * 3);

        // The full history is downsampled into the stored peaks on finish; the
        // reactive copy is only the tail that the recording bar displays.
        this._allLevels.push(level);
        this.recordingLevels = this._allLevels.slice(-WAVEFORM_BAR_COUNT);
      };

      this._audioContext = audioContext;
      this._levelFrameId = requestAnimationFrame(sample);
    },

    // Stops the mic and meters without dropping the captured take.
    _stopCapture() {
      clearInterval(this._recordingTimerId);
      this._recordingTimerId = null;

      if (this._levelFrameId) {
        cancelAnimationFrame(this._levelFrameId);
        this._levelFrameId = null;
      }

      this._audioContext?.close().catch(() => {});
      this._audioContext = null;

      // Releasing the tracks is what clears the browser's recording indicator.
      this._recordingStream?.getTracks().forEach((track) => track.stop());
      this._recordingStream = null;
      this._mediaRecorder = null;
    },

    _teardownRecording() {
      this._stopCapture();
      clearTimeout(this._noticeTimeoutId);
      this._noticeTimeoutId = null;
      this._finishing = false;

      this.isRecording = false;
      this.isPaused = false;
      this.recordingLimitReached = false;
      this.recordingNotice = null;
      this.recordingSeconds = 0;
      this.recordingLevels = [];
      this.recordedWaveform = null;
      this.recordedDuration = null;
      this._allLevels = [];
      this._recordingChunks = [];
      this._recordedBytes = 0;
      this._elapsedBeforePause = 0;
      this._segmentStartedAt = 0;
    },
  };
}
